import { Instance, SnapshotIn, applySnapshot, flow, getParent, types } from 'mobx-state-tree';

import { ICreateCgClass, getCgClass, removeCgClass, updateCgClass } from '@common/api/cgClasses';

import { ICgClassesModel } from './CgClasses';

const CgClassSchool = types.model('CgClassSchool', {
	id: types.maybe(types.number),
	c_g_city_id: types.maybeNull(types.number),
	name: types.maybeNull(types.string),
	custom_city_name: types.maybeNull(types.string),
	created_at: types.maybeNull(types.string),
	updated_at: types.maybeNull(types.string),
	deleted_at: types.maybeNull(types.string),
});

const CgClassProgress = types.model('CgClassProgress', {
	not_started: types.optional(types.number, 0),
	in_progress: types.optional(types.number, 0),
	completed: types.optional(types.number, 0),
});

const CgClass = types
	.model('CgClass', {
		id: types.maybe(types.maybeNull(types.number)),
		name: types.maybeNull(types.string),
		c_g_coordinator_id: types.maybe(types.maybeNull(types.number)),
		c_g_school_id: types.maybe(types.maybeNull(types.number)),
		code: types.maybeNull(types.string),
		school: types.maybeNull(CgClassSchool),
		students_count: types.maybeNull(types.number),
		progress: types.optional(types.maybeNull(CgClassProgress), {
			not_started: 0,
			in_progress: 0,
			completed: 0,
		}),
	})
	.volatile(() => ({
		loading: false,
	}))
	.actions((self) => ({
		fetch: flow(function* (id: number) {
			self.loading = true;
			try {
				const result = yield getCgClass(id || self.id);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		remove: flow(function* () {
			self.loading = true;
			try {
				yield removeCgClass(self.id);
				const parent = getParent<ICgClassesModel>(self, 2);
				parent.removeItem(self as ICgClassModel);
			} catch (e) {
				self.loading = false;
			}
		}),
		save: flow(function* (data: ICreateCgClass) {
			self.loading = true;
			try {
				const result = yield updateCgClass(self.id, data);

				applySnapshot(self, result);
			} catch (e) {
				throw e?.response?.data ?? e;
			} finally {
				self.loading = false;
			}
		}),
	}));

export default CgClass;

export interface ICgClassModel extends Instance<typeof CgClass> {}
export type ICgClassSnapshotIn = SnapshotIn<typeof CgClass>;
